import { isTokenExpired } from '@/core/utils/jwt.utils';
import type { AuthenticatedUser } from '@/features/auth/types/authenticated-user.type';

export interface StoredUser {
  userId: string;
  displayName: string;
  email: string;
  accessToken: string;
  refreshToken: string;
  emailVerified: boolean;
  avatarUrl: string | null;
  permissions: string[];
  role: { roleId: string; name: string } | null;
}

export function reconstructUser(userData: StoredUser): AuthenticatedUser | null {
  try {
    console.log('🔍 [RECONSTRUCT] Starting user reconstruction', {
      hasUserData: !!userData,
      userId: userData?.userId,
      hasAccessToken: !!userData?.accessToken,
    });

    if (!userData || typeof userData !== 'object') {
      console.warn('❌ [RECONSTRUCT] Invalid user data - not an object');
      return null;
    }

    if (!userData.userId || !userData.accessToken) {
      console.warn('❌ [RECONSTRUCT] Missing required fields', {
        hasUserId: !!userData.userId,
        hasAccessToken: !!userData.accessToken,
      });
      return null;
    }

    const tokenExpired = isTokenExpired({ value: userData.accessToken });
    console.log('🔍 [RECONSTRUCT] Token expiration check', {
      tokenExpired,
      userId: userData.userId,
    });

    if (tokenExpired) {
      console.warn('❌ [RECONSTRUCT] Token expired, clearing user data');
      return null;
    }

    console.log('✅ [RECONSTRUCT] User reconstruction successful', {
      userId: userData.userId,
      email: userData.email,
    });

    return {
      userId: userData.userId,
      displayName: userData.displayName,
      email: userData.email,
      accessToken: userData.accessToken,
      refreshToken: userData.refreshToken,
      emailVerified: userData.emailVerified,
      avatarUrl: userData.avatarUrl ?? undefined,
      permissions: userData.permissions ?? [],
      role: userData.role ?? null,
    };
  } catch (error) {
    console.error('❌ [RECONSTRUCT] Error reconstructing user:', error);
    return null;
  }
}
